import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  CalendarDays,
  MapPin,
  Check,
  X,
  HelpCircle,
  Minus,
  Plus,
  Trash2,
  Navigation,
  Armchair,
  Pencil,
} from 'lucide-react';
import type {
  PublicInvite,
  PublicRsvpMemberInput,
  PublicRsvpAnswerInput,
  RsvpAnswerValue,
} from '@peles/shared';
import {
  googleCalendarUrl,
  outlookCalendarUrl,
  yahooCalendarUrl,
  directionsUrl,
  invitePreviewUrl,
  missingRequiredAnswers,
  namedComing,
  othersComing,
} from '@peles/shared';
import { getPublicInvite, respondPublicInvite } from '../api/publicRsvp';
import { hebrewDateString } from '../utils/hebrewCalendar';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { EnvelopeReveal } from '../components/invites/EnvelopeReveal';
import { InvitationPages } from '../components/invites/InvitationPages';
import { ShareLinkButton, PublicPageFooter, TransparentImage } from '../components/common';
import { SelfRsvpForm } from '../components/invites/SelfRsvpForm';
import { AttendingToggle } from '../components/guests/AttendingToggle';
import { ChoiceChips } from '../components/invites/ChoiceChips';

const DEFAULT_ACCENT_COLOR = '#1a365d';

type ResponseChoice = 'YES' | 'NO' | 'MAYBE';

/** Compact Yes/No/Maybe toggle used for sub-event responses. */
function ChoiceToggle({
  value,
  onChange,
  allowMaybe,
  name,
}: {
  value: ResponseChoice | null;
  onChange: (choice: ResponseChoice) => void;
  allowMaybe: boolean;
  name: string;
}) {
  const options: ResponseChoice[] = allowMaybe ? ['YES', 'MAYBE', 'NO'] : ['YES', 'NO'];
  const labels: Record<ResponseChoice, string> = { YES: 'Yes', MAYBE: 'Maybe', NO: 'No' };
  return (
    <div className="flex gap-1.5">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          aria-label={`${labels[option]} to ${name}`}
          aria-pressed={value === option}
          onClick={() => onChange(option)}
          className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${
            value === option
              ? 'border-primary-600 bg-primary-50 text-primary-700'
              : 'border-gray-200 text-gray-600 hover:border-gray-300'
          }`}
        >
          {labels[option]}
        </button>
      ))}
    </div>
  );
}

/** Whether the guest may say who of the party is coming. */
function allowPartyEditOf(invite: PublicInvite | null): boolean {
  return invite?.settings?.allowPartyEdit !== false;
}

/** Somebody named and not yet answered for is shown — and counted — as coming. */
function withDefaultAttending<T extends { attending?: boolean | null }>(member: T) {
  return { ...member, attending: member.attending !== false };
}

function formatEventDate(startAt: Date | string, endAt: Date | string | null): string {
  const start = new Date(startAt);
  const startText = start.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  if (!endAt) return startText;
  const end = new Date(endAt);
  if (end.toDateString() === start.toDateString()) return startText;
  const endText = end.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  return `${startText} – ${endText}`;
}

function CountStepper({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-base sm:text-sm text-gray-700">{label}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          disabled={disabled || value <= 0}
          onClick={() => onChange(value - 1)}
          className="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 disabled:opacity-40"
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className="w-6 text-center font-semibold text-gray-900">{value}</span>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          disabled={disabled || value >= 100}
          onClick={() => onChange(value + 1)}
          className="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center rounded-full border border-gray-300 text-gray-600 disabled:opacity-40"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export function PublicRsvpPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Deep link from the email buttons (?r=YES|NO|MAYBE) pre-selects the answer.
  const presetResponse = searchParams.get('r');

  const [invite, setInvite] = useState<PublicInvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [choice, setChoice] = useState<ResponseChoice | null>(null);
  // The people coming nobody named; the head count is these plus the named
  // people marked coming (or, with no names, these alone).
  const [otherAdults, setOtherAdults] = useState(0);
  const [otherKids, setOtherKids] = useState(0);
  const [notes, setNotes] = useState('');
  const [subChoices, setSubChoices] = useState<Record<string, ResponseChoice>>({});
  /**
   * `takenFrom`: a name added on this page that took somebody out of the
   * others, and which of them — so it gives back exactly what it took.
   */
  type MemberDraft = PublicRsvpMemberInput & {
    hasEmail?: boolean;
    takenFrom?: 'ADULT' | 'CHILD';
  };
  const [members, setMembers] = useState<MemberDraft[]>([]);
  const [answers, setAnswers] = useState<Record<string, RsvpAnswerValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    // A new token is a different invitation, so nothing from the previous one
    // may survive — otherwise an in-app hop between invitations could show a
    // stale thank-you or error screen, or a response the guest never gave.
    setLoading(true);
    setLoadError(null);
    setInvite(null);
    setChoice(null);
    setOtherAdults(0);
    setOtherKids(0);
    setNotes('');
    setSubChoices({});
    setMembers([]);
    setAnswers({});
    setSubmitted(false);
    setSubmitError(null);
    getPublicInvite(token)
      .then((data) => {
        if (cancelled) return;
        setInvite(data);
        // Guest-less share link: nothing personal to prefill.
        if (!data.guest) return;
        const answered = !!data.response && data.response.response !== 'PENDING';
        const declined = data.response?.response === 'NO';
        if (answered && data.response) {
          setChoice(data.response.response as ResponseChoice);
          setNotes(data.response.notes ?? '');
        }
        // Somebody named and not yet answered for counts as coming, as the
        // page shows them; after a "no" everybody is asked afresh.
        const loadedMembers: MemberDraft[] = (data.partyMembers ?? []).map((member) => ({
          id: member.id,
          name: member.name,
          memberType: member.memberType,
          // Stored emails are never sent to the page — track presence only.
          hasEmail: member.hasEmail,
          mealOptionId: member.mealOptionId,
          dietaryNotes: member.dietaryNotes ?? undefined,
          attending: declined ? null : (member.attending ?? null),
        }));
        const counts =
          answered && data.response && !declined
            ? { adults: data.response.adultCount, kids: data.response.kidCount }
            : { adults: data.guest.adultCount, kids: data.guest.kidCount };
        const others = othersComing(
          loadedMembers.map(withDefaultAttending),
          counts.adults,
          counts.kids,
        );
        setOtherAdults(others.adults);
        setOtherKids(others.kids);
        const initialSubChoices: Record<string, ResponseChoice> = {};
        for (const subEvent of data.subEvents ?? []) {
          if (subEvent.response !== 'PENDING') {
            initialSubChoices[subEvent.eventId] = subEvent.response as ResponseChoice;
          }
        }
        setSubChoices(initialSubChoices);
        setMembers(loadedMembers);
        const initialAnswers: Record<string, RsvpAnswerValue> = {};
        for (const question of data.questions ?? []) {
          if (question.answer) initialAnswers[question.id] = question.answer;
        }
        setAnswers(initialAnswers);

        // The email deep link is an explicit intent — let it win over any
        // stored response so the guest lands on the answer they clicked.
        const preset = presetResponse?.toUpperCase();
        if (
          preset === 'YES' ||
          preset === 'NO' ||
          (preset === 'MAYBE' && data.settings?.allowMaybe !== false)
        ) {
          setChoice(preset as ResponseChoice);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setLoadError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, presetResponse]);

  const namedMembers = members.filter((member) => member.name.trim());
  const named = namedComing(namedMembers.map(withDefaultAttending));
  const headCount = { adults: named.adults + otherAdults, kids: named.kids + otherKids };

  /**
   * Somebody the guest names here was among the others already counted —
   * naming them moves them from the others to the names, never adds a person,
   * as long as there is somebody of their kind among the others to be.
   * Only a name added on this page: a stored name was never among the others.
   * A name remembers which of the others it took (`takenFrom`), so clearing,
   * removing or retyping it gives back exactly that. Coming or not is left
   * out of it; that is the guest answering, and moves the total.
   */
  const settle = (member: MemberDraft): MemberDraft => {
    const wants = !member.id && member.name.trim() ? (member.memberType ?? 'ADULT') : undefined;
    if (member.takenFrom === wants) return member;
    let adults = otherAdults;
    let kids = otherKids;
    if (member.takenFrom === 'CHILD') kids += 1;
    else if (member.takenFrom === 'ADULT') adults += 1;
    let takenFrom: MemberDraft['takenFrom'];
    if (wants === 'CHILD' && kids > 0) {
      kids -= 1;
      takenFrom = 'CHILD';
    } else if (wants === 'ADULT' && adults > 0) {
      adults -= 1;
      takenFrom = 'ADULT';
    }
    setOtherAdults(adults);
    setOtherKids(kids);
    return { ...member, takenFrom };
  };

  const updateMember = (index: number, updates: Partial<MemberDraft>) => {
    const before = members[index];
    if (!before) return;
    const after = settle({ ...before, ...updates });
    setMembers((current) => current.map((member, i) => (i === index ? after : member)));
  };

  const removeMember = (index: number) => {
    const before = members[index];
    if (before) settle({ ...before, name: '' });
    setMembers((current) => current.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!token || !choice) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const answersList: PublicRsvpAnswerInput[] = Object.entries(answers)
        .filter(([, value]) => Object.keys(value).length > 0)
        .map(([questionId, value]) => ({ questionId, value }));
      // The celebrations are asked only of somebody coming — a "no" hides
      // them — so an answer left from before is not sent behind a "no".
      const subResponses =
        choice === 'NO'
          ? []
          : Object.entries(subChoices).map(([eventId, response]) => ({ eventId, response }));
      // Only send the members list when the party section is in play —
      // an unconditional [] would wipe members managed elsewhere.
      const membersRelevant =
        (invite?.mealOptions?.length ?? 0) > 0 ||
        members.length > 0 ||
        (invite?.partyMembers?.length ?? 0) > 0;
      await respondPublicInvite(token, {
        response: choice,
        adultCount: headCount.adults,
        kidCount: headCount.kids,
        notes: notes || undefined,
        ...(subResponses.length ? { responses: subResponses } : {}),
        ...(membersRelevant
          ? {
              partyMembers: members
                .filter((member) => member.name.trim())
                .map(
                  ({
                    hasEmail: _hasEmail,
                    takenFrom: _takenFrom,
                    email,
                    attending,
                    ...member
                  }) => ({
                    ...member,
                    ...(email ? { email } : {}),
                    // Everybody named says whether they are coming — an
                    // unanswered name is coming, as the page showed them.
                    ...(allowPartyEditOf(invite) ? { attending: attending !== false } : {}),
                  }),
                ),
            }
          : {}),
        ...(answersList.length ? { answers: answersList } : {}),
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  // The event's own logo doubles as the tab/home-screen icon, so a saved or
  // reopened invitation is recognizable. (Chat-app previews come from the
  // server-rendered /i/:token page — crawlers never run this.)
  usePageMetadata({ title: invite?.eventName, iconUrl: invite?.eventLogoUrl });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-lg mx-auto space-y-4 animate-pulse" role="status">
          <span className="sr-only">Loading invitation…</span>
          <div className="h-64 rounded-2xl bg-gray-200" />
          <div className="rounded-2xl bg-white p-6 space-y-3">
            <div className="mx-auto h-6 w-2/3 rounded bg-gray-200" />
            <div className="mx-auto h-4 w-1/2 rounded bg-gray-100" />
            <div className="mx-auto h-4 w-1/3 rounded bg-gray-100" />
          </div>
          <div className="rounded-2xl bg-white p-6 space-y-2">
            <div className="h-12 rounded-xl bg-gray-100" />
            <div className="h-12 rounded-xl bg-gray-100" />
          </div>
        </div>
      </div>
    );
  }

  if (loadError || !invite) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-md">
          <h1 className="text-xl font-semibold text-gray-900">Invitation not found</h1>
          <p className="mt-2 text-sm text-gray-500">
            This invitation link is invalid or no longer active. Please contact your host.
          </p>
        </div>
      </div>
    );
  }

  const guestName = invite.guest
    ? [invite.guest.firstName, invite.guest.lastName].filter(Boolean).join(' ')
    : '';
  const allowMaybe = invite.settings?.allowMaybe !== false;
  const allowPartyEdit = allowPartyEditOf(invite);
  const perPerson = allowPartyEdit && namedMembers.length > 0;
  // The party is shown whenever it has names — to be read, if not edited —
  // and to a guest who may add names, meals on offer or not.
  const hasMeals = (invite.mealOptions?.length ?? 0) > 0;
  const partySection = allowPartyEdit || hasMeals || members.length > 0;
  // A required question owes an answer from anybody coming; a "no" owes none.
  const missingAnswers =
    choice && choice !== 'NO' ? missingRequiredAnswers(invite.questions ?? [], answers) : [];
  const missingPrompts = (invite.questions ?? [])
    .filter((question) => missingAnswers.includes(question.id))
    .map((question) => question.prompt);
  const nobodyComing =
    !!choice && choice !== 'NO' && allowPartyEdit && headCount.adults + headCount.kids < 1;
  const closed = invite.deadlinePassed;
  const accent = invite.settings?.accentColor || DEFAULT_ACCENT_COLOR;
  const backgroundColor = invite.settings?.backgroundColor || undefined;
  // Information-only campaigns, and the guest-less share link, never collect
  // a response — the page is then purely the invitation.
  const canRespond = invite.canRespond !== false && !!invite.guest;
  // A forwarded link can still collect an RSVP — from someone who tells us who
  // they are and joins the guest list by doing so.
  const canSelfRsvp = !invite.guest && invite.canSelfRsvp === true;
  const artworkPages = invite.artworkPages ?? (invite.artworkUrl ? [invite.artworkUrl] : []);
  const shareUrl = token ? invitePreviewUrl(window.location.origin, token) : '';

  const choices: { value: ResponseChoice; label: string; icon: typeof Check }[] = [
    { value: 'YES', label: 'Joyfully Accept', icon: Check },
    ...(allowMaybe ? [{ value: 'MAYBE' as const, label: 'Maybe', icon: HelpCircle }] : []),
    { value: 'NO', label: 'Regretfully Decline', icon: X },
  ];

  const calendarEvent = {
    title: invite.eventName,
    start: invite.eventStartAt,
    end: invite.eventEndAt,
    location: [invite.venueName, invite.venueAddress].filter(Boolean).join(', ') || null,
  };
  // The campaign's letter text is the invitation's own wording for itself, so
  // it titles the page as well as the letter. Records the guest keeps — the
  // calendar entry, the share text, the tab title — stay on the event's real
  // name, which is short and identifies the event.
  const headline = invite.settings?.envelopeMessage?.trim() || invite.eventName;
  const mapsUrl = directionsUrl(invite.venueAddress || invite.venueName);
  const icsUrl = token ? `/api/v1/rsvp/${encodeURIComponent(token)}/calendar.ics` : '#';

  return (
    <div
      className={`min-h-screen py-8 px-4${backgroundColor ? '' : ' bg-gray-50'}`}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {/* Keyed on the token so the envelope and the artwork deck remount for a
          different invitation — their "already opened" and current-page state
          is internal and would otherwise carry over. */}
      <EnvelopeReveal
        key={token}
        accentColor={accent}
        envelopeColor={invite.settings?.envelopeColor}
        sealColor={invite.settings?.sealColor}
        message={headline}
        sealImageUrl={invite.settings?.envelopeSealLogo ? invite.eventLogoUrl : null}
        disabled={invite.settings?.showEnvelope === false}
      >
        <div className="max-w-lg mx-auto space-y-4">
          {artworkPages.length > 0 && (
            <InvitationPages
              pages={artworkPages}
              eventName={invite.eventName}
              accentColor={accent}
            />
          )}

          <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
            {invite.settings?.includeEventLogo && invite.eventLogoUrl && (
              <TransparentImage
                src={invite.eventLogoUrl}
                alt="Event logo"
                className="mx-auto mb-3 max-h-20 w-auto object-contain"
              />
            )}
            <h1 className="text-2xl font-serif" style={{ color: accent }}>
              {headline}
            </h1>
            <div className="mt-3 flex items-center justify-center gap-2 text-sm text-gray-600">
              <CalendarDays className="w-4 h-4" />
              <span>{formatEventDate(invite.eventStartAt, invite.eventEndAt)}</span>
            </div>
            {invite.settings?.showHebrewDate && (
              <div className="mt-0.5 text-sm text-gray-600" dir="rtl">
                {hebrewDateString(new Date(invite.eventStartAt))}
              </div>
            )}
            {invite.venueName && (
              <div className="mt-1 flex items-center justify-center gap-2 text-sm text-gray-600">
                <MapPin className="w-4 h-4" />
                <span>
                  {invite.venueName}
                  {invite.venueAddress ? ` · ${invite.venueAddress}` : ''}
                </span>
              </div>
            )}
            {invite.message && (
              <p className="mt-4 text-sm text-gray-700 whitespace-pre-line">{invite.message}</p>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs">
              <span className="inline-flex items-center gap-1 text-gray-500">
                <CalendarDays className="w-3.5 h-3.5" /> Add to calendar:
              </span>
              <a
                href={googleCalendarUrl(calendarEvent)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium hover:underline"
                style={{ color: accent }}
              >
                Google
              </a>
              <a
                href={outlookCalendarUrl(calendarEvent)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium hover:underline"
                style={{ color: accent }}
              >
                Outlook
              </a>
              <a
                href={yahooCalendarUrl(calendarEvent)}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium hover:underline"
                style={{ color: accent }}
              >
                Yahoo
              </a>
              <a href={icsUrl} className="font-medium hover:underline" style={{ color: accent }}>
                Apple / .ics
              </a>
            </div>
            {mapsUrl && (
              <div className="mt-2">
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
                  style={{ color: accent }}
                >
                  <Navigation className="w-3.5 h-3.5" /> Get directions
                </a>
              </div>
            )}
          </div>

          {invite.timeline && invite.timeline.sections.some((s) => s.entries.length > 0) && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-center text-lg font-serif" style={{ color: accent }}>
                {invite.timeline.name}
              </h2>
              {invite.timeline.description && (
                <p className="mt-1 text-center text-sm text-gray-500">
                  {invite.timeline.description}
                </p>
              )}
              <div className="mt-4 space-y-4">
                {invite.timeline.sections
                  .filter((section) => section.entries.length > 0)
                  .map((section) => (
                    <div key={section.id}>
                      <h3
                        className="text-xs font-semibold uppercase tracking-wide"
                        style={{ color: accent }}
                      >
                        {section.name}
                      </h3>
                      <ul className="mt-1 space-y-1.5">
                        {section.entries.map((entry) => (
                          <li key={entry.id} className="flex gap-3 text-sm">
                            {entry.time && (
                              <span className="w-16 shrink-0 font-medium" style={{ color: accent }}>
                                {entry.time}
                              </span>
                            )}
                            <span className="min-w-0">
                              <span className="text-gray-900">{entry.title}</span>
                              {entry.location && (
                                <span className="block text-xs text-gray-500">
                                  {entry.location}
                                </span>
                              )}
                              {entry.notes && (
                                <span className="block text-xs text-gray-500">{entry.notes}</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {canSelfRsvp && token ? (
            <SelfRsvpForm
              token={token}
              allowMaybe={allowMaybe}
              allowPartyEdit={allowPartyEdit}
              accentColor={accent}
              // They now have their own invitation: continue there so meals,
              // party members and the host's questions are available, with the
              // answer they just gave pre-selected.
              onResponded={(personalToken, response) =>
                navigate(`/rsvp/${personalToken}?r=${response}`, { replace: true })
              }
            />
          ) : !canRespond ? (
            <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
              <p className="text-sm text-gray-600">
                {invite.guest
                  ? 'This invitation is for your information — no reply is needed.'
                  : 'You are warmly invited. Contact your host to confirm your attendance.'}
              </p>
            </div>
          ) : submitted ? (
            <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-gray-900">Thank you, {guestName}!</h2>
              <p className="mt-1 text-sm text-gray-500">
                Your response has been recorded. You can revisit this link to update it any time.
              </p>
              <div className="mt-5 grid gap-2 sm:grid-cols-2">
                {choice !== 'NO' && (
                  <>
                    <a
                      href={googleCalendarUrl(calendarEvent)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 min-h-11 rounded-xl border text-sm font-medium"
                      style={{ borderColor: accent, color: accent }}
                    >
                      <CalendarDays className="w-4 h-4" /> Add to Google Calendar
                    </a>
                    <a
                      href={icsUrl}
                      className="inline-flex items-center justify-center gap-2 min-h-11 rounded-xl border text-sm font-medium"
                      style={{ borderColor: accent, color: accent }}
                    >
                      <CalendarDays className="w-4 h-4" /> Apple / Outlook (.ics)
                    </a>
                  </>
                )}
                {choice !== 'NO' && invite.settings?.showSeatFinder && token && (
                  <Link
                    to={`/rsvp/${encodeURIComponent(token)}/seat`}
                    className="inline-flex items-center justify-center gap-2 min-h-11 rounded-xl border text-sm font-medium"
                    style={{ borderColor: accent, color: accent }}
                  >
                    <Armchair className="w-4 h-4" /> Find my seat
                  </Link>
                )}
                {!closed && (
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="inline-flex items-center justify-center gap-2 min-h-11 rounded-xl border border-gray-300 text-sm font-medium text-gray-700"
                  >
                    <Pencil className="w-4 h-4" /> Change my answer
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-gray-900">
                  {guestName}, will you attend?
                </h2>
                {invite.rsvpDeadline && !closed && (
                  <p className="mt-1 text-xs text-gray-500">
                    Please respond by{' '}
                    {new Date(invite.rsvpDeadline).toLocaleDateString('en-US', {
                      dateStyle: 'long',
                    })}
                  </p>
                )}
              </div>

              {closed ? (
                <p className="text-center text-sm text-amber-700 bg-amber-50 rounded-lg py-3 px-4">
                  The RSVP deadline has passed. Please contact your host directly.
                </p>
              ) : (
                <>
                  <div className="grid gap-2">
                    {choices.map(({ value, label, icon: Icon }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setChoice(value)}
                        aria-pressed={choice === value}
                        className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition-colors ${
                          choice === value
                            ? ''
                            : 'border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                        style={
                          choice === value
                            ? { borderColor: accent, color: accent, backgroundColor: `${accent}14` }
                            : undefined
                        }
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    ))}
                  </div>

                  {choice && choice !== 'NO' && allowPartyEdit && (
                    <div className="border-t border-gray-100 pt-3">
                      <h3 className="text-sm font-medium text-gray-900">Who&apos;s coming?</h3>
                      {perPerson && (
                        <ul className="mt-1 divide-y divide-gray-100">
                          {members.map((member, index) =>
                            member.name.trim() ? (
                              <li
                                key={member.id ?? `new-${index}`}
                                className="flex items-center justify-between gap-2 py-2"
                              >
                                <span className="min-w-0 truncate text-sm text-gray-800">
                                  {member.name}
                                  {member.memberType === 'CHILD' && (
                                    <span className="ml-1 text-xs text-gray-500">(child)</span>
                                  )}
                                </span>
                                <AttendingToggle
                                  name={member.name}
                                  accent={accent}
                                  coming={member.attending !== false}
                                  onChange={(coming) => updateMember(index, { attending: coming })}
                                />
                              </li>
                            ) : null,
                          )}
                        </ul>
                      )}
                      {perPerson && (
                        <p className="mt-2 text-xs text-gray-500">Anyone else, not named above:</p>
                      )}
                      <CountStepper
                        label={perPerson ? 'Other adults' : 'Adults'}
                        value={otherAdults}
                        onChange={setOtherAdults}
                      />
                      <CountStepper
                        label={perPerson ? 'Other children' : 'Children'}
                        value={otherKids}
                        onChange={setOtherKids}
                      />
                      {perPerson && (
                        <p className="text-sm text-gray-700" aria-live="polite">
                          <span className="font-medium">Total coming:</span>{' '}
                          {headCount.adults + headCount.kids}
                        </p>
                      )}
                      {nobodyComing && (
                        <p className="mt-1 text-sm text-amber-700" role="alert">
                          Nobody is marked as coming — choose who is, or decline.
                        </p>
                      )}
                    </div>
                  )}

                  {choice && choice !== 'NO' && (invite.subEvents?.length ?? 0) > 0 && (
                    <div className="border-t border-gray-100 pt-3 space-y-2">
                      <h3 className="text-sm font-medium text-gray-900">
                        Which celebrations will you join?
                      </h3>
                      {(invite.subEvents ?? []).map((subEvent) => (
                        <div
                          key={subEvent.eventId}
                          className="flex items-center justify-between gap-2"
                        >
                          <div className="min-w-0">
                            <div className="text-sm text-gray-800 truncate">{subEvent.name}</div>
                            <div className="text-xs text-gray-500">
                              {new Date(subEvent.startAt).toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </div>
                          </div>
                          <ChoiceToggle
                            name={subEvent.name}
                            allowMaybe={allowMaybe}
                            value={subChoices[subEvent.eventId] ?? null}
                            onChange={(response) =>
                              setSubChoices((current) => ({
                                ...current,
                                [subEvent.eventId]: response,
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {choice && choice !== 'NO' && partySection && (
                    <div className="border-t border-gray-100 pt-3 space-y-2">
                      <h3 className="text-sm font-medium text-gray-900">
                        {hasMeals ? 'Your party & meals' : 'Your party'}
                      </h3>
                      {members.length === 0 && (
                        <p className="text-sm text-gray-500">
                          Add the names of the people coming with you
                          {hasMeals ? ' and choose their meals' : ''}.
                        </p>
                      )}
                      {members.map((member, index) => (
                        <div
                          key={index}
                          className="rounded-xl border border-gray-200 p-3 space-y-2 sm:space-y-0 sm:flex sm:flex-wrap sm:items-center sm:gap-2"
                          data-testid="party-member-card"
                        >
                          <div className="flex items-center gap-2 sm:flex-1 sm:min-w-28">
                            <input
                              type="text"
                              value={member.name}
                              onChange={(e) => updateMember(index, { name: e.target.value })}
                              readOnly={!allowPartyEdit}
                              placeholder="Name"
                              aria-label={`Party member ${index + 1} name`}
                              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm"
                            />
                            {allowPartyEdit && (
                              <button
                                type="button"
                                aria-label={`Remove party member ${index + 1}`}
                                onClick={() => removeMember(index)}
                                className="p-2 text-gray-400 hover:text-red-600 sm:order-last"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <input
                            type="email"
                            value={member.email ?? ''}
                            onChange={(e) =>
                              updateMember(index, { email: e.target.value || undefined })
                            }
                            readOnly={!allowPartyEdit}
                            placeholder={member.hasEmail ? 'Email on file' : 'Email (optional)'}
                            aria-label={`Party member ${index + 1} email`}
                            className="w-full sm:w-auto sm:flex-1 sm:min-w-32 px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm"
                          />
                          <ChoiceChips
                            label={`Party member ${index + 1} type`}
                            options={[
                              { value: 'ADULT', label: 'Adult' },
                              { value: 'CHILD', label: 'Child' },
                            ]}
                            value={member.memberType ?? 'ADULT'}
                            onChange={(memberType) => updateMember(index, { memberType })}
                            disabled={!allowPartyEdit}
                            accent={accent}
                          />
                          {hasMeals && (
                            <div className="w-full">
                              <span className="block text-xs text-gray-500 mb-1">Meal</span>
                              <ChoiceChips
                                label={`Party member ${index + 1} meal`}
                                options={(invite.mealOptions ?? []).map((meal) => ({
                                  value: meal.id,
                                  label: meal.name,
                                }))}
                                value={member.mealOptionId ?? null}
                                onChange={(mealOptionId) => updateMember(index, { mealOptionId })}
                                accent={accent}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                      {allowPartyEdit && (
                        <button
                          type="button"
                          onClick={() =>
                            setMembers((current) => [
                              ...current,
                              { name: '', memberType: 'ADULT', attending: true },
                            ])
                          }
                          className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800"
                        >
                          <Plus className="w-4 h-4" />
                          Add person
                        </button>
                      )}
                    </div>
                  )}

                  {choice && (invite.questions?.length ?? 0) > 0 && (
                    <div className="border-t border-gray-100 pt-3 space-y-3">
                      {(invite.questions ?? []).map((question) => (
                        <div key={question.id}>
                          <label
                            htmlFor={`q-${question.id}`}
                            className="block text-sm font-medium text-gray-700"
                          >
                            {question.prompt}
                            {question.isRequired && <span className="text-red-500"> *</span>}
                          </label>
                          {question.questionType === 'TEXT' && (
                            <input
                              id={`q-${question.id}`}
                              type="text"
                              value={answers[question.id]?.text ?? ''}
                              onChange={(e) =>
                                setAnswers((current) => ({
                                  ...current,
                                  [question.id]: { text: e.target.value },
                                }))
                              }
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm"
                            />
                          )}
                          {question.questionType === 'NUMBER' && (
                            <input
                              id={`q-${question.id}`}
                              type="number"
                              value={answers[question.id]?.number ?? ''}
                              onChange={(e) =>
                                setAnswers((current) => ({
                                  ...current,
                                  [question.id]:
                                    e.target.value === '' ? {} : { number: Number(e.target.value) },
                                }))
                              }
                              inputMode="numeric"
                              className="mt-1 w-32 px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm"
                            />
                          )}
                          {question.questionType === 'YES_NO' && (
                            <div className="mt-2">
                              <ChoiceChips
                                id={`q-${question.id}`}
                                label={question.prompt}
                                options={[
                                  { value: 'true', label: 'Yes' },
                                  { value: 'false', label: 'No' },
                                ]}
                                value={
                                  answers[question.id]?.bool === undefined
                                    ? null
                                    : String(answers[question.id]!.bool)
                                }
                                onChange={(value) =>
                                  setAnswers((current) => ({
                                    ...current,
                                    [question.id]: { bool: value === 'true' },
                                  }))
                                }
                                accent={accent}
                              />
                            </div>
                          )}
                          {question.questionType === 'SINGLE_CHOICE' && (
                            <div className="mt-2">
                              <ChoiceChips
                                id={`q-${question.id}`}
                                label={question.prompt}
                                options={(question.config?.choices ?? []).map((option) => ({
                                  value: option,
                                  label: option,
                                }))}
                                value={answers[question.id]?.choice ?? null}
                                onChange={(value) =>
                                  setAnswers((current) => ({
                                    ...current,
                                    [question.id]: { choice: value },
                                  }))
                                }
                                accent={accent}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {choice && (
                    <div>
                      <label
                        htmlFor="rsvp-notes"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Note to the host (optional)
                      </label>
                      <textarea
                        id="rsvp-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-base sm:text-sm"
                      />
                    </div>
                  )}

                  {submitError && (
                    <p className="text-sm text-red-600 text-center" role="alert">
                      {submitError}
                    </p>
                  )}

                  {missingPrompts.length > 0 && (
                    <p className="text-sm text-amber-700 text-center" role="status">
                      Please answer: {missingPrompts.join(', ')}
                    </p>
                  )}

                  <button
                    type="button"
                    disabled={!choice || submitting || nobodyComing || missingAnswers.length > 0}
                    onClick={() => void handleSubmit()}
                    className="w-full py-3 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                    style={{ backgroundColor: accent }}
                  >
                    {submitting ? 'Submitting…' : 'Submit RSVP'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Only links that cannot submit a response are offered for sharing —
            forwarding a personal RSVP link would let someone else answer for
            the guest. Info-only invitations and the campaign share link are
            meant to be passed around. */}
          {shareUrl && !canRespond && (
            <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
              <h2 className="text-sm font-medium text-gray-900">Share this invitation</h2>
              <p className="mt-1 text-xs text-gray-500">
                Anyone you send this link to sees the invitation with the event details.
              </p>
              <div className="mt-3 flex justify-center">
                <ShareLinkButton
                  url={shareUrl}
                  title={invite.eventName}
                  text={`You're invited: ${invite.eventName} —`}
                  accentColor={accent}
                />
              </div>
            </div>
          )}

          <PublicPageFooter />
        </div>
      </EnvelopeReveal>
    </div>
  );
}
